// src/components/admin/AdminOrganizationsPanel.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "../../i18n";
import api from "../../api/api";
import { athenaColors } from "../../theme/palette";
import { glass, glassTableSx } from "../../theme/glassTokens";
import { athenaDarkPaperSx } from "../layout/AthenaDialogStyles";

interface AdminOrganization {
  id: string;
  name: string;
  subscription_tier: string | null;
  subscription_tier_display: string | null;
  tier_max_environments: number | null;
  custom_max_environments: number | null;
  custom_monthly_token_limit: number | null;
  environment_count: number;
  member_count: number;
}

interface AdminTierOption {
  name: string;
  display_name: string;
}

const AdminOrganizationsPanel: React.FC = () => {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();

  const [orgs, setOrgs] = useState<AdminOrganization[]>([]);
  const [tiers, setTiers] = useState<AdminTierOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog state
  const [editOrg, setEditOrg] = useState<AdminOrganization | null>(null);
  const [tierDraft, setTierDraft] = useState("");
  const [capDraft, setCapDraft] = useState("");
  const [tokenDraft, setTokenDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [orgRes, tierRes] = await Promise.all([
        api.get("/billing/admin/organizations/"),
        api.get("/billing/admin/tiers/"),
      ]);
      setOrgs(orgRes.data || []);
      setTiers(
        (tierRes.data || []).map((tr: any) => ({
          name: tr.name,
          display_name: tr.display_name,
        })),
      );
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          t(
            "admin.organizations.error_load",
            "Failed to load organizations.",
          ),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openEdit = (org: AdminOrganization) => {
    setEditOrg(org);
    setTierDraft(org.subscription_tier || "");
    setCapDraft(
      org.custom_max_environments != null
        ? String(org.custom_max_environments)
        : "",
    );
    setTokenDraft(
      org.custom_monthly_token_limit != null
        ? String(org.custom_monthly_token_limit)
        : "",
    );
    setError(null);
  };

  const closeEdit = () => {
    setEditOrg(null);
  };

  const handleSave = async () => {
    if (!editOrg) return;
    // PATCH only the changed fields (see backend contract in cursor_logs/074).
    const payload: Record<string, string | number> = {};

    const origTier = editOrg.subscription_tier || "";
    if (tierDraft && tierDraft !== origTier) {
      payload.subscription_tier = tierDraft;
    }

    const origCap =
      editOrg.custom_max_environments != null
        ? String(editOrg.custom_max_environments)
        : "";
    if (capDraft !== origCap) {
      payload.custom_max_environments =
        capDraft === "" ? "" : parseInt(capDraft, 10);
    }

    const origToken =
      editOrg.custom_monthly_token_limit != null
        ? String(editOrg.custom_monthly_token_limit)
        : "";
    if (tokenDraft !== origToken) {
      payload.custom_monthly_token_limit =
        tokenDraft === "" ? "" : parseInt(tokenDraft, 10);
    }

    if (Object.keys(payload).length === 0) {
      closeEdit();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.patch(`/billing/admin/organizations/${editOrg.id}/`, payload);
      closeEdit();
      await fetchData();
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          t("admin.organizations.error_save", "Failed to update organization."),
      );
    } finally {
      setSaving(false);
    }
  };

  // The effective environment allowance, formatted "count / cap" where the cap
  // is the per-org override when present, otherwise the tier default. 0 = ∞.
  const renderEnvironments = (org: AdminOrganization) => {
    const hasOverride = org.custom_max_environments != null;
    const capValue = hasOverride
      ? org.custom_max_environments
      : org.tier_max_environments;

    let capLabel: string;
    if (capValue == null) {
      capLabel = t("admin.organizations.env_no_cap", "no cap");
    } else if (capValue === 0) {
      capLabel = hasOverride
        ? t("admin.organizations.env_unlimited_cap", "∞ cap")
        : t("admin.organizations.env_unlimited_tier", "tier ∞");
    } else {
      capLabel = hasOverride
        ? t("admin.organizations.env_cap", "{{n}} cap").replace(
            "{{n}}",
            String(capValue),
          )
        : t("admin.organizations.env_tier", "tier {{n}}").replace(
            "{{n}}",
            String(capValue),
          );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
        <Typography
          component="span"
          variant="body2"
          sx={{ fontWeight: 700, color: textPrimary }}
        >
          {org.environment_count}
        </Typography>
        <Typography
          component="span"
          variant="caption"
          sx={{ color: textSecondary }}
        >
          / {capLabel}
        </Typography>
      </Box>
    );
  };

  const renderTokenLimit = (org: AdminOrganization) => {
    if (org.custom_monthly_token_limit == null) {
      return (
        <Typography variant="caption" sx={{ color: textSecondary }}>
          {t("admin.organizations.token_tier_default", "Tier default")}
        </Typography>
      );
    }
    return (
      <Typography variant="body2" sx={{ fontWeight: 600, color: textPrimary }}>
        {(org.custom_monthly_token_limit / 1_000_000).toFixed(1)}
        {t("admin.organizations.token_millions_suffix", "M / mo")}
      </Typography>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${athenaColors.deepNavy}, ${athenaColors.royalBlue})`,
              display: "flex",
              alignItems: "center",
            }}
          >
            <ApartmentIcon
              sx={{ color: theme.palette.common.white, fontSize: 22 }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: textPrimary }}
            >
              {t("admin.organizations.title", "Organizations")}
            </Typography>
            <Typography variant="caption" sx={{ color: textSecondary }}>
              {t(
                "admin.organizations.subtitle",
                "Assign tiers and per-org limits (env cap = consortium contracted centre count).",
              )}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.light, 0.08),
            border: `1px solid ${alpha(theme.palette.error.light, 0.2)}`,
          }}
        >
          <Typography variant="body2" color="error.main">
            {error}
          </Typography>
        </Box>
      )}

      {/* Organization table */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : orgs.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ textAlign: "center", py: 3, color: textSecondary }}
          >
            {t("admin.organizations.empty", "No organizations found.")}
          </Typography>
        ) : (
          <Table size="small" sx={glassTableSx}>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("admin.organizations.col_name", "Name")}
                </TableCell>
                <TableCell>
                  {t("admin.organizations.col_tier", "Tier")}
                </TableCell>
                <TableCell>
                  {t("admin.organizations.col_environments", "Environments")}
                </TableCell>
                <TableCell>
                  {t("admin.organizations.col_token_limit", "Token limit")}
                </TableCell>
                <TableCell align="right">
                  {t("admin.organizations.col_members", "Members")}
                </TableCell>
                <TableCell align="right">
                  {t("admin.organizations.col_actions", "Actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orgs.map((org) => (
                <TableRow
                  key={org.id}
                  sx={{ "&:hover": { backgroundColor: glass.rowHover } }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: textPrimary }}
                    >
                      {org.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: textPrimary }}>
                      {org.subscription_tier_display ||
                        t("admin.organizations.tier_none", "—")}
                    </Typography>
                  </TableCell>
                  <TableCell>{renderEnvironments(org)}</TableCell>
                  <TableCell>{renderTokenLimit(org)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ color: textPrimary }}>
                      {org.member_count}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={t(
                        "admin.organizations.edit_tooltip",
                        "Edit tier and per-org limits",
                      )}
                    >
                      <IconButton
                        size="small"
                        onClick={() => openEdit(org)}
                        sx={{ color: textSecondary }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* Edit dialog. Dialogs render in a PORTAL, so the shared dark paper is
          required to keep the title/body ink consistent with the glass tokens. */}
      <Dialog
        open={editOrg != null}
        onClose={closeEdit}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: athenaDarkPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: glass.textPrimary }}>
          {editOrg
            ? t("admin.organizations.edit_title", "Edit {{name}}").replace(
                "{{name}}",
                editOrg.name,
              )
            : t("admin.organizations.edit_title_generic", "Edit organization")}
        </DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}
        >
          <FormControl fullWidth size="small">
            <InputLabel id="org-tier-label">
              {t("admin.organizations.field_tier", "Subscription tier")}
            </InputLabel>
            <Select
              labelId="org-tier-label"
              label={t("admin.organizations.field_tier", "Subscription tier")}
              value={tierDraft}
              onChange={(e) => setTierDraft(e.target.value)}
            >
              {tiers.map((tier) => (
                <MenuItem key={tier.name} value={tier.name}>
                  {tier.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            fullWidth
            label={t("admin.organizations.field_env_cap", "Environment cap")}
            type="number"
            value={capDraft}
            onChange={(e) => setCapDraft(e.target.value)}
            helperText={t(
              "admin.organizations.field_env_cap_help",
              "Blank = use the tier default; a number is the per-deal cap (0 = unlimited).",
            )}
            inputProps={{ min: 0 }}
          />

          <TextField
            size="small"
            fullWidth
            label={t(
              "admin.organizations.field_token_limit",
              "Monthly token limit",
            )}
            type="number"
            value={tokenDraft}
            onChange={(e) => setTokenDraft(e.target.value)}
            helperText={t(
              "admin.organizations.field_token_limit_help",
              "Blank = use the tier default.",
            )}
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>
            {t("action.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {t("action.save", "Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminOrganizationsPanel;
